import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Crosshair, MapPin, X, Check, Trash2, Loader2, Navigation,
} from "lucide-react";
import { buildCalibration, metersToSheetRadius } from "@/lib/gpsCalibration";
import { writeCalibrationCache, readCalibrationCache } from "@/lib/calibrationCache";
import StationChip from "@/components/object/StationChip";

/**
 * GpsOverlay — the "I am standing roughly HERE on the plans" feature.
 *
 * Two modes:
 *  1. Live GPS — once the sheet has ≥2 calibration anchors, browser
 *     geolocation is converted to a normalized sheet (x, y) and drawn as
 *     a blue location dot + accuracy ring inside the SheetCanvas.
 *  2. Manual "I'm here" — long-press on the drawing always works (no
 *     calibration needed) and drops a temporary location dot.
 *
 * Calibration UI:
 *  - "Add anchor" mode: user taps the drawing → small inline form opens
 *    asking for known lat/lng (or "Use my current GPS").
 *  - ≥2 anchors enables Live GPS.
 *  - List of anchors with delete buttons.
 *
 * Per the design directive: this is operational orientation, not survey.
 * No coordinate-system selection, no UTM, no projection picker. Hide complexity.
 */

const STORAGE_PREFIX = "fop_gps_panel_";

export function useGpsCalibration(sheetId) {
  const [cal, setCal] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!sheetId) return;
    let cancelled = false;
    (async () => {
      // Iter 34.1 — offline-resilient read path. Try the network first
      // (so admin edits propagate immediately when online); on failure
      // fall back to IndexedDB (survives SW unregister + storage purge).
      // The service worker also caches the response HTTP-layer.
      try {
        const { data } = await api.get(`/sheets/${sheetId}/calibration`);
        if (cancelled) return;
        setCal(data);
        setLoaded(true);
        // Persist for offline reuse. Fire-and-forget; never blocks UI.
        writeCalibrationCache(sheetId, data);
      } catch {
        const cached = await readCalibrationCache(sheetId);
        if (cancelled) return;
        setCal(cached || { points: [] });
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [sheetId]);

  const save = async (points) => {
    const { data } = await api.put(`/sheets/${sheetId}/calibration`, { points });
    setCal(data);
    writeCalibrationCache(sheetId, data);
    return data;
  };
  const clear = async () => {
    await api.delete(`/sheets/${sheetId}/calibration`);
    const empty = { points: [] };
    setCal(empty);
    writeCalibrationCache(sheetId, empty);
  };

  return { cal, loaded, save, clear };
}

/* Live position polling — exposed as a hook so the dot can also render
 * in Field Mode without mounting the panel. */
export function useLivePosition(active) {
  const [pos, setPos] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!active || typeof navigator === "undefined" || !navigator.geolocation) {
      setPos(null); setError(null);
      return;
    }
    let cancelled = false;
    const watch = navigator.geolocation.watchPosition(
      (p) => {
        if (cancelled) return;
        setPos({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
          heading: p.coords.heading,
          ts: p.timestamp,
        });
        setError(null);
      },
      (err) => { if (!cancelled) setError(err?.message || "GPS unavailable"); },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 12000 },
    );
    return () => { cancelled = true; navigator.geolocation.clearWatch(watch); };
  }, [active]);
  return { pos, error };
}

/* ----------------------------------------------------------------- */
/* LocationDot — the SheetCanvas overlay (placed inside the stage)    */
/* ----------------------------------------------------------------- */
export function GpsLocationLayer({ transform, position, manual, scale }) {
  const items = [];
  if (transform && position) {
    const xy = transform.gpsToSheet(position.lat, position.lng);
    if (xy && xy.nx >= 0 && xy.nx <= 1 && xy.ny >= 0 && xy.ny <= 1) {
      const radius = position.accuracy
        ? metersToSheetRadius(transform, position.lat, position.lng, position.accuracy)
        : 0;
      items.push(
        <SmoothedLiveDot
          key="live"
          target={{ nx: xy.nx, ny: xy.ny, heading: position.heading, ts: position.ts || 0 }}
          radiusN={radius}
          scale={scale}
        />,
      );
    }
  }
  if (manual) {
    items.push(<ManualDot key="manual" nx={manual.nx} ny={manual.ny} scale={scale} />);
  }
  return <>{items}</>;
}

/**
 * SmoothedLiveDot — eliminates the GPS "snap" between samples.
 *
 *  - Each new (nx, ny) target is reached via rAF tween over ~600ms with an
 *    easeOutCubic curve, so the user sees natural sliding instead of jumping.
 *  - Heading is either (a) taken from `target.heading` when the browser
 *    provides one (with a low-pass filter to soften jitter), or (b) inferred
 *    from the displacement vector between recent samples when the browser
 *    omits it (stationary or low-precision devices report null).
 *  - The heading cone is rendered only when heading is known AND the dot has
 *    moved meaningfully in the last few seconds — a stationary super doesn't
 *    need a direction indicator (that would just spin from GPS noise).
 *
 * This is operational orientation only — NOT survey-grade. Lightweight enough
 * to run on iPad in Field Mode without battery cost.
 */
function SmoothedLiveDot({ target, radiusN, scale }) {
  const [shown, setShown] = React.useState(() => ({ nx: target.nx, ny: target.ny }));
  const [smoothedHeading, setSmoothedHeading] = React.useState(null);
  const [moving, setMoving] = React.useState(false);

  // Refs so the rAF closure always sees the freshest target / animation state.
  const startRef = React.useRef({ nx: target.nx, ny: target.ny, t: performance.now() });
  const targetRef = React.useRef(target);
  const rafRef = React.useRef(0);
  // Recent samples (last ~4) for displacement-based heading + motion gate.
  const samplesRef = React.useRef([]);

  React.useEffect(() => {
    // New target arrived — start a tween from current shown to target.nx,ny.
    targetRef.current = target;
    startRef.current = { nx: shown.nx, ny: shown.ny, t: performance.now() };
    const samples = samplesRef.current;
    samples.push({ nx: target.nx, ny: target.ny, t: target.ts || performance.now() });
    while (samples.length > 5) samples.shift();

    // Motion gate: only flag "moving" if the dot has shifted ≥0.0008 of the
    // sheet (~tens of feet onsite at typical sheet scales) in the last 6s.
    const now = target.ts || performance.now();
    const recent = samples.filter((s) => now - s.t <= 6000);
    let maxDelta = 0;
    for (let i = 1; i < recent.length; i++) {
      const dx = recent[i].nx - recent[i - 1].nx;
      const dy = recent[i].ny - recent[i - 1].ny;
      maxDelta = Math.max(maxDelta, Math.hypot(dx, dy));
    }
    setMoving(maxDelta > 0.0008);

    // Heading: prefer browser-supplied; otherwise derive from displacement
    // vector across the last two samples (in sheet-normalized space — which
    // is fine because the heading cone is a visual indicator, not a bearing).
    let nextHeading = null;
    if (Number.isFinite(target.heading)) {
      nextHeading = target.heading;
    } else if (recent.length >= 2) {
      const a = recent[recent.length - 2];
      const b = recent[recent.length - 1];
      const dx = b.nx - a.nx;
      const dy = b.ny - a.ny;
      if (Math.hypot(dx, dy) > 0.0003) {
        // atan2(dy,dx) returns radians; we want compass-like degrees with
        // 0° pointing UP (north). Sheet y increases DOWN.
        nextHeading = (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;
      }
    }
    if (nextHeading != null) {
      setSmoothedHeading((prev) => {
        if (prev == null) return nextHeading;
        // Low-pass: take 30% of the new sample, keep 70% of last. Handles the
        // 0/360 wrap by picking the shorter angular direction.
        let delta = nextHeading - prev;
        while (delta > 180) delta -= 360;
        while (delta < -180) delta += 360;
        return (prev + delta * 0.3 + 360) % 360;
      });
    }

    // rAF tween — eliminate any in-flight one.
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const DUR = 600;
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now2) => {
      const k = Math.min(1, (now2 - startRef.current.t) / DUR);
      const e = easeOutCubic(k);
      const tg = targetRef.current;
      setShown({
        nx: startRef.current.nx + (tg.nx - startRef.current.nx) * e,
        ny: startRef.current.ny + (tg.ny - startRef.current.ny) * e,
      });
      if (k < 1) rafRef.current = requestAnimationFrame(step);
      else rafRef.current = 0;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.nx, target.ny, target.ts, target.heading]);

  return (
    <LiveDot
      nx={shown.nx}
      ny={shown.ny}
      radiusN={radiusN}
      scale={scale}
      heading={smoothedHeading}
      moving={moving}
    />
  );
}

function LiveDot({ nx, ny, radiusN, scale, heading = null, moving = false }) {
  // Heading cone — soft fan emanating from the dot in the direction of travel.
  // Only rendered when we have a heading AND motion in the last 6s. The cone
  // uses a conic-gradient to avoid SVG mounting cost for a transient indicator.
  const showCone = moving && Number.isFinite(heading);
  // Length-in-image-pixels; divide by scale so the cone stays consistent on
  // screen regardless of zoom.
  const CONE_R = 60 / scale;
  return (
    <div
      className="absolute pointer-events-none"
      style={{ left: `${nx * 100}%`, top: `${ny * 100}%`, transform: "translate(-50%, -50%)" }}
      data-testid="gps-live-dot"
      data-viewer-overlay="1"
      data-overlay-type="gps-marker"
      data-overlay-anchor-nx={nx}
      data-overlay-anchor-ny={ny}
      data-heading={Number.isFinite(heading) ? Math.round(heading) : ""}
      data-moving={moving ? "1" : "0"}
    >
      {radiusN > 0.001 && (
        <div
          className="absolute rounded-full"
          style={{
            width: `${radiusN * 2 * 100}vw`,
            height: `${radiusN * 2 * 100}vw`,
            left: "50%", top: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(37, 99, 235, 0.18)",
            border: "1px solid rgba(37, 99, 235, 0.45)",
          }}
        />
      )}
      {showCone && (
        <div
          className="absolute pointer-events-none"
          data-testid="gps-heading-cone"
          data-viewer-overlay="1"
          data-overlay-type="gps-heading-cone"
          style={{
            left: "50%", top: "50%",
            width: CONE_R * 2,
            height: CONE_R * 2,
            // Rotate so 0° = north (up) — translate to align with our heading axis.
            transform: `translate(-50%, -50%) rotate(${heading}deg)`,
            // 44° fan around the heading axis, blue-tinted to match the dot.
            background:
              "conic-gradient(from -22deg, " +
              "rgba(37, 99, 235, 0) 0deg, " +
              "rgba(37, 99, 235, 0.32) 8deg, " +
              "rgba(37, 99, 235, 0.32) 36deg, " +
              "rgba(37, 99, 235, 0) 44deg, " +
              "rgba(37, 99, 235, 0) 360deg)",
            // Fade outward via a radial mask.
            WebkitMaskImage: "radial-gradient(circle at center, black 16%, transparent 72%)",
            maskImage: "radial-gradient(circle at center, black 16%, transparent 72%)",
            borderRadius: "50%",
          }}
        />
      )}
      <div
        className="rounded-full ring-2 ring-white shadow-lg animate-pulse"
        style={{
          width: 18 / scale, height: 18 / scale,
          background: "#2563eb",
        }}
      />
    </div>
  );
}

function ManualDot({ nx, ny, scale }) {
  return (
    <div
      className="absolute pointer-events-none flex flex-col items-center"
      style={{ left: `${nx * 100}%`, top: `${ny * 100}%`, transform: "translate(-50%, -100%)" }}
      data-testid="gps-manual-dot"
      data-viewer-overlay="1"
      data-overlay-type="gps-manual-marker"
      data-overlay-anchor-nx={nx}
      data-overlay-anchor-ny={ny}
    >
      <div
        className="font-mono uppercase tracking-wider text-white bg-black/85 px-1.5 py-0.5 rounded mb-0.5 whitespace-nowrap"
        style={{ fontSize: 9 / scale * 1, transform: `scale(${1 / scale})`, transformOrigin: "bottom center" }}
      >
        I'm here
      </div>
      <div
        className="rounded-full ring-2 ring-white shadow-lg"
        style={{
          width: 16 / scale, height: 16 / scale,
          background: "#f59e0b",
        }}
      />
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* GpsPanel — the calibration/control panel                          */
/* ----------------------------------------------------------------- */
export default function GpsPanel({
  sheetId, sheetNumber,
  gpsEnabled, onToggle,
  cal, onSaveCalibration, onClearCalibration,
  livePosition, liveError,
  manualPosition, onClearManual,
  awaitingAnchorTap, onAwaitAnchorTap, anchorTapPos, onCancelAnchorTap,
  onClose,
}) {
  const [creatingFromGps, setCreatingFromGps] = useState(false);
  const [draftLat, setDraftLat] = useState("");
  const [draftLng, setDraftLng] = useState("");
  const [draftLabel, setDraftLabel] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset draft fields when the anchor tap changes.
  useEffect(() => {
    if (anchorTapPos) {
      setDraftLat(""); setDraftLng(""); setDraftLabel(""); setCreatingFromGps(false);
    }
  }, [anchorTapPos]);

  const calBuilt = useMemo(() => buildCalibration(cal?.points || []), [cal?.points]);
  const transform = calBuilt.ok ? calBuilt.transform : null;

  const ready = (cal?.points?.length || 0) >= 2 && !!transform;
  // GH-15 (real-plan certification, 2026-10-01) — a rejected fit (bad
  // anchor geometry) used to look identical to "not enough anchors
  // placed yet" ("Needs 0 more anchors" while staying stuck). Surface
  // the real rejection reason instead.
  const calibrationIssue = !calBuilt.ok && (cal?.points?.length || 0) >= 2 ? calBuilt.reason : null;

  const saveAnchor = async () => {
    if (!anchorTapPos) return;
    let lat = parseFloat(draftLat);
    let lng = parseFloat(draftLng);
    if (creatingFromGps) {
      if (!livePosition) return;
      lat = livePosition.lat;
      lng = livePosition.lng;
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setBusy(true);
    try {
      const newPoint = {
        nx: anchorTapPos.nx, ny: anchorTapPos.ny,
        lat, lng,
        label: draftLabel.trim() || null,
      };
      await onSaveCalibration([...(cal?.points || []), newPoint]);
      onCancelAnchorTap();
    } finally {
      setBusy(false);
    }
  };

  const removeAnchor = async (idx) => {
    const next = (cal?.points || []).filter((_, i) => i !== idx);
    if (next.length === 0) {
      await onClearCalibration();
    } else {
      await onSaveCalibration(next);
    }
  };

  return (
    <div
      className="fixed top-12 right-3 z-30 w-[300px] max-w-[calc(100vw-1.5rem)] rounded-lg shadow-2xl border border-[var(--fop-border)] bg-[var(--fop-surface)]"
      data-testid="gps-panel"
    >
      <div className="px-3.5 py-2.5 flex items-center justify-between gap-2 border-b border-[var(--fop-border)]">
        <div className="flex items-center gap-2 min-w-0">
          <Navigation size={13} className="text-[var(--fop-text-2)]" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-2)]">
            Field position · {sheetNumber}
          </div>
        </div>
        <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" aria-label="Close" data-testid="gps-panel-close">
          <X size={14} />
        </button>
      </div>

      <div className="px-3.5 py-3 space-y-3 max-h-[70vh] overflow-y-auto">
        {/* Toggle row */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--fop-text)] font-medium">Live GPS</div>
            <div className="text-[10px] text-[var(--fop-text-3)] mt-0.5" data-testid="gps-ready-status">
              {ready
                ? (gpsEnabled ? "Tracking…" : "Ready — field-grade, not survey")
                : calibrationIssue
                  ? `Calibration rejected — ${calibrationIssue}`
                  : `Needs ${2 - (cal?.points?.length || 0)} more anchor${(2 - (cal?.points?.length || 0)) === 1 ? "" : "s"}`}
            </div>
          </div>
          <button
            onClick={onToggle}
            disabled={!ready}
            className={`relative w-10 h-5 rounded-full transition-colors ${gpsEnabled && ready ? "bg-[var(--fop-primary)]" : "bg-[var(--fop-border-hi)]"} disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="gps-live-toggle"
            aria-label="Toggle live GPS"
          >
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${gpsEnabled && ready ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>

        {gpsEnabled && ready && livePosition && (
          <div className="text-[10px] font-mono text-[var(--fop-text-3)] leading-snug space-y-1" data-testid="gps-readout">
            <div>
              {livePosition.lat.toFixed(6)}, {livePosition.lng.toFixed(6)}
              {livePosition.accuracy ? <span className="ml-2">· ±{Math.round(livePosition.accuracy)}m</span> : null}
            </div>
            {(() => {
              const xy = transform?.gpsToSheet?.(livePosition.lat, livePosition.lng);
              if (!xy) return null;
              return (
                <StationChip
                  sheetId={sheetId}
                  nx={xy.nx}
                  ny={xy.ny}
                  compact
                  testId="gps-station-chip"
                />
              );
            })()}
          </div>
        )}
        {manualPosition && (
          <div className="text-[10px] font-mono leading-snug" data-testid="gps-manual-station">
            <StationChip
              sheetId={sheetId}
              nx={manualPosition.nx}
              ny={manualPosition.ny}
              compact
              testId="gps-manual-station-chip"
            />
          </div>
        )}
        {gpsEnabled && liveError && (
          <div className="text-[10px] text-[var(--st-escalated)]" data-testid="gps-error">{liveError}</div>
        )}

        {manualPosition && (
          <div className="flex items-center justify-between text-[10px] font-mono" data-testid="gps-manual-row">
            <span className="text-[var(--fop-text-2)]">
              Manual mark @ {(manualPosition.nx * 100).toFixed(1)}%, {(manualPosition.ny * 100).toFixed(1)}%
            </span>
            <button onClick={onClearManual} className="text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]" data-testid="gps-manual-clear">
              Clear
            </button>
          </div>
        )}

        {/* Anchor list */}
        <div className="border-t border-[var(--fop-border)] pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
              Anchors · {(cal?.points || []).length}
            </div>
            {(cal?.points || []).length > 0 && (
              <button
                onClick={onClearCalibration}
                className="text-[10px] text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] font-mono"
                data-testid="gps-clear-all"
              >
                Clear all
              </button>
            )}
          </div>
          <ul className="space-y-1">
            {(cal?.points || []).map((p, i) => (
              <li
                key={`${i}-${p.lat}`}
                className="flex items-center gap-2 text-[11px] py-1.5 px-2 rounded bg-[var(--fop-surface-2)]"
                data-testid={`gps-anchor-${i}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[var(--fop-primary)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[var(--fop-text)] truncate">
                    {p.label || `Anchor ${i + 1}`}
                  </div>
                  <div className="font-mono text-[9px] text-[var(--fop-text-3)] truncate">
                    {p.lat.toFixed(5)}, {p.lng.toFixed(5)} ·
                    sheet {(p.nx * 100).toFixed(1)}%, {(p.ny * 100).toFixed(1)}%
                  </div>
                </div>
                <button
                  onClick={() => removeAnchor(i)}
                  className="text-[var(--fop-text-4)] hover:text-[var(--st-escalated)] shrink-0"
                  aria-label="Remove anchor"
                  data-testid={`gps-anchor-remove-${i}`}
                >
                  <Trash2 size={11} />
                </button>
              </li>
            ))}
            {(cal?.points || []).length === 0 && (
              <li className="text-[10px] text-[var(--fop-text-3)] italic">
                Add at least 2 anchors to enable live GPS.
              </li>
            )}
          </ul>

          {/* Add-anchor flow */}
          {!anchorTapPos && (
            <button
              onClick={onAwaitAnchorTap}
              className={`mt-2 w-full px-2 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider border flex items-center gap-1.5 justify-center transition-colors ${
                awaitingAnchorTap
                  ? "border-[var(--fop-primary)] text-[var(--fop-primary)] bg-[var(--fop-primary-soft)]"
                  : "border-[var(--fop-border)] text-[var(--fop-text-2)] hover:border-[var(--fop-text-3)]"
              }`}
              data-testid="gps-add-anchor-start"
            >
              <Crosshair size={11} />
              {awaitingAnchorTap ? "Tap the drawing at a known point…" : "Add anchor point"}
            </button>
          )}

          {anchorTapPos && (
            <div className="mt-2 border border-[var(--fop-primary)] bg-[var(--fop-primary-soft)] rounded p-2 space-y-2" data-testid="gps-anchor-form">
              <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)]">
                Anchor @ {(anchorTapPos.nx * 100).toFixed(1)}%, {(anchorTapPos.ny * 100).toFixed(1)}%
              </div>
              <input
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                placeholder="Optional label (e.g. NW corner of catch basin)"
                className="w-full text-[11px] py-1 px-1.5"
                data-testid="gps-anchor-label"
              />
              {!creatingFromGps && (
                <div className="grid grid-cols-2 gap-1.5">
                  <input
                    value={draftLat}
                    onChange={(e) => setDraftLat(e.target.value)}
                    placeholder="Lat"
                    className="w-full text-[11px] py-1 px-1.5 font-mono"
                    data-testid="gps-anchor-lat"
                  />
                  <input
                    value={draftLng}
                    onChange={(e) => setDraftLng(e.target.value)}
                    placeholder="Lng"
                    className="w-full text-[11px] py-1 px-1.5 font-mono"
                    data-testid="gps-anchor-lng"
                  />
                </div>
              )}
              {creatingFromGps && livePosition && (
                <div className="text-[10px] font-mono text-[var(--fop-text-2)]">
                  Using current GPS: {livePosition.lat.toFixed(6)}, {livePosition.lng.toFixed(6)}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCreatingFromGps((v) => !v)}
                  className="flex-1 text-[11px] font-mono uppercase tracking-wider py-1 px-1.5 rounded border border-[var(--fop-border)] hover:border-[var(--fop-text-3)] text-[var(--fop-text-2)] flex items-center justify-center gap-1"
                  data-testid="gps-anchor-use-current"
                >
                  <Crosshair size={10} />
                  {creatingFromGps ? "Type manually" : "Use my GPS"}
                </button>
              </div>
              <div className="flex items-center gap-1.5 pt-1">
                <button
                  onClick={onCancelAnchorTap}
                  className="text-[11px] font-mono uppercase tracking-wider py-1 px-2 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                  data-testid="gps-anchor-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAnchor}
                  disabled={busy || (!creatingFromGps && (!draftLat || !draftLng)) || (creatingFromGps && !livePosition)}
                  className="ml-auto text-[11px] font-mono uppercase tracking-wider py-1 px-2.5 rounded bg-[var(--fop-primary)] text-black disabled:opacity-40 flex items-center gap-1"
                  data-testid="gps-anchor-save"
                >
                  {busy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                  Save
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="text-[10px] text-[var(--fop-text-4)] leading-snug border-t border-[var(--fop-border)] pt-2.5">
          <MapPin size={9} className="inline -mt-0.5 mr-0.5" />
          Operational orientation only. Long-press anywhere on the drawing for a manual “I'm here” marker.
        </div>
      </div>
    </div>
  );
}
